import type { InstallationType } from "@/base/sensor-configurations"
import type {
  DeviceStatusData,
  SensorStatusBadges,
} from "@/redux/device-event-last.reducer"

/**
 * Visual + semantic state of a device row, used to color the status dot
 * and the optional accent bar.
 *
 * `idle` covers the common case of a healthy device that is currently doing
 * nothing — relay open, no flow, pivot stopped, reel parked. We want it to
 * read as "fine, just inactive" rather than "online and busy".
 */
export type DeviceRowState =
  | "online"
  | "idle"
  | "offline"
  | "unconfigured"
  | "unreachable"
  | "batteryCritical"
  | "stoppedShort"

/**
 * Installation types whose relay command is informative (energized = the
 * device is actively doing the thing). pump_off_only is intentionally
 * excluded — its relay only commands stop, so it isn't an activity signal.
 */
const RELAY_DRIVEN_TYPES = new Set<InstallationType>([
  "pump",
  "pump_on_off",
  "pump_vfd",
  "valve",
])

/**
 * Is the device currently *doing* its primary job? Used to distinguish
 * `online` (active) from `idle` (online but at rest).
 *
 * Per-type signal:
 *   - Pumps + valves with an informative relay: relay energized.
 *   - pump_off_only: no informative relay; fall back to a positive flow
 *     reading.
 *   - Reels / pivots / linears / travelers: the monitor's motion sensor.
 *     Pivots also count the controller's run command as activity so an
 *     intent-to-run shows up before the wheel actually starts turning.
 *   - Anything else (prototype sensor builds, etc.): considered active when
 *     it's reporting at all — there's no "off" state to surface as idle.
 */
function isDeviceActive({
  badges,
  deviceInstallationType,
  isInMotion,
}: {
  badges: SensorStatusBadges
  deviceInstallationType: InstallationType
  isInMotion: boolean
}): boolean {
  if (RELAY_DRIVEN_TYPES.has(deviceInstallationType)) {
    return badges.relay?.colorScheme === "$success"
  }
  if (deviceInstallationType === "pump_off_only") {
    return badges.flow?.colorScheme === "$success"
  }
  if (
    deviceInstallationType === "reel" ||
    deviceInstallationType === "reel_with_booster_off_only"
  ) {
    return isInMotion || badges.reel?.colorScheme === "$info"
  }
  if (
    deviceInstallationType === "center_pivot" ||
    deviceInstallationType === "center_pivot_controller" ||
    deviceInstallationType === "center_pivot_monitor"
  ) {
    return isInMotion || badges.pivotRun?.colorScheme === "$success"
  }
  if (
    deviceInstallationType === "linear_move" ||
    deviceInstallationType === "traveller_soft"
  ) {
    return isInMotion
  }
  return true
}

/**
 * Derive the single visual state for the leading status dot.
 *
 * Precedence (highest first):
 *   stoppedShort > unconfigured > unreachable > batteryCritical > offline >
 *   online (active) / idle (no current irrigation activity)
 *
 * A reel that's stopped short is an actionable failure mode that the user
 * needs to see above all else — even offline or unconfigured, because the
 * last known state still represents an interrupted run that needs attention.
 */
export function getDeviceRowState({
  badges,
  batteryState,
  deviceInstallationType,
  deviceStatus,
  isInMotion,
  isStoppedShort,
  now,
  unreachableUntil,
}: Pick<
  DeviceStatusData,
  | "badges"
  | "batteryState"
  | "deviceInstallationType"
  | "deviceStatus"
  | "isInMotion"
  | "isStoppedShort"
  | "unreachableUntil"
> & {
  now?: Date
}): DeviceRowState {
  if (isStoppedShort) {
    return "stoppedShort"
  }
  if (deviceInstallationType === "unconfigured") {
    return "unconfigured"
  }
  if (
    typeof unreachableUntil === "string" &&
    new Date(unreachableUntil) > (now ?? new Date())
  ) {
    return "unreachable"
  }
  if (batteryState === "BES") {
    return "batteryCritical"
  }
  if (deviceStatus === "offline") {
    return "offline"
  }
  return isDeviceActive({ badges, deviceInstallationType, isInMotion })
    ? "online"
    : "idle"
}

/**
 * Keys of SensorStatusBadges whose value is always a StatusBadgeProps —
 * i.e. excluding `vfdStatus` which is a VfdIndicatorProps.
 */
export type PrimaryStatusBadgeKey = Exclude<
  keyof SensorStatusBadges,
  "vfdStatus"
>

/**
 * Per-installation-type list of badge keys to render in the compact row,
 * in priority order. The first 1–2 that have data in `badges` are shown.
 *
 * Battery and voltage are intentionally omitted from this list — they are
 * promoted into the leading dot when critical (BES) and are otherwise
 * available via the expanded panel.
 */
export const PRIMARY_BADGE_KEYS_BY_TYPE: Record<
  InstallationType,
  PrimaryStatusBadgeKey[]
> = {
  center_pivot: ["pivotRun", "pivotDirection", "pivotEndGun"],
  center_pivot_controller: ["pivotRun", "pivotDirection", "pivotEndGun"],
  center_pivot_monitor: ["pivotRun", "pressure"],
  linear_move: ["wheel", "gpsHeading"],
  // depth and flow are the headline readings for prototype sensor builds;
  // pressure ranks below.
  prototype: ["depth", "flow", "pressure", "temperature"],
  // Pumps: depth (when configured) is the operator's headline reading and
  // ranks above the relay so a relay-off pump still surfaces depth. Relay
  // (on/off) is next, then flow (preferred over pressure as the validation
  // signal). The cap-bump in selectPrimaryStatusBadgeKeys keeps relay visible
  // alongside depth when the relay is energized.
  pump: ["depth", "relay", "flow", "pressure"],
  // Off-only pumps: the relay only commands stop, so it isn't informative —
  // skip it and lead with depth, then flow.
  pump_off_only: ["depth", "flow", "pressure"],
  pump_on_off: ["depth", "relay", "flow", "pressure"],
  pump_vfd: ["depth", "relay", "flow", "pressure"],
  // Reels: state, then validation (pressure), then run-ETA, then orientation.
  reel: ["reel", "pressure", "runEta", "gpsHeading"],
  // Booster relay on a reel is off-only and not informative.
  reel_with_booster_off_only: ["reel", "pressure", "runEta", "gpsHeading"],
  traveller_soft: ["wheel", "gpsHeading"],
  unconfigured: [],
  valve: ["relay"],
}

/**
 * Maximum number of badges shown in the collapsed row by default. Keeping
 * this tight preserves horizontal space on narrow phones; the rest live in
 * the expanded panel.
 *
 * Some device states bump the cap (see `selectPrimaryStatusBadgeKeys`).
 */
export const MAX_PRIMARY_BADGES = 1

/**
 * Pump-family types whose relay is informative (not off-only). When the
 * relay on one of these is energized, we render a second validation badge
 * (flow/pressure) alongside the relay.
 */
const PUMP_TYPES_WITH_INFORMATIVE_RELAY = new Set<InstallationType>([
  "pump",
  "pump_on_off",
  "pump_vfd",
])

/**
 * Is the relay currently energized?
 *
 * The relay badge is constructed in the reducer with `colorScheme: "$success"`
 * when state is "ON" and no colorScheme otherwise — see `getRelayBadgeProps`
 * in `device-event-last.reducer.ts`.
 */
function isRelayOn(badges: SensorStatusBadges): boolean {
  return badges.relay?.colorScheme === "$success"
}

/**
 * Pick which badges to render in the compact row for a given device.
 *
 * Pure: no React, no side effects. Easy to unit-test the routing logic.
 *
 * Special rule for pumps + VFD pumps: when the relay is ON, include the next
 * validation badge (flow > pressure) alongside the relay. The "OFF" label is
 * short enough that horizontal space is only a real constraint when the
 * pump is running, and the validation signal is most informative then.
 *
 * Special rule for on-off pumps: pressure is the user's primary "is it
 * actually pressurizing?" signal, so always surface it when configured —
 * even when the relay is OFF (a dropped reading after a stop is meaningful).
 */
export function selectPrimaryStatusBadgeKeys({
  badges,
  deviceInstallationType,
}: {
  badges: SensorStatusBadges
  deviceInstallationType: InstallationType
}): PrimaryStatusBadgeKey[] {
  // Tolerate unknown installation types (e.g. fixture data with a legacy
  // string value) so the compact row degrades to no badges instead of
  // throwing and crashing the whole screen.
  const candidateOrder =
    PRIMARY_BADGE_KEYS_BY_TYPE[deviceInstallationType] ?? []
  const available = candidateOrder.filter((key) => Boolean(badges[key]))

  // Trigger the second-badge cap-bump whenever the relay is energized and in
  // the priority list — not just when it's the leading badge. Depth ranks
  // above relay for pumps, so the relay can sit in the second slot and we
  // still want the run-state indicator visible alongside the depth reading.
  const showSecondBadge =
    PUMP_TYPES_WITH_INFORMATIVE_RELAY.has(deviceInstallationType) &&
    available.includes("relay") &&
    isRelayOn(badges)

  const cap = showSecondBadge ? MAX_PRIMARY_BADGES + 1 : MAX_PRIMARY_BADGES
  const result = available.slice(0, cap)

  if (
    deviceInstallationType === "pump_on_off" &&
    Boolean(badges.pressure) &&
    !result.includes("pressure")
  ) {
    result.push("pressure")
  }
  return result
}

/**
 * Global fallback ordering for badges that don't appear in any device type's
 * priority list. These are rendered after the per-type priority keys in the
 * expanded status view.
 *
 * Order rationale: device health (battery, controller status) first because
 * it gates everything else; then primary sensors a user typically cares about
 * (relay/flow/pressure); then orientation; then env/secondary.
 */
const EXPANDED_BADGE_FALLBACK_ORDER: PrimaryStatusBadgeKey[] = [
  "batteryVoltageExternal",
  "batteryStateOfChargeInternal",
  "relay",
  "device",
  "gps",
  "flow",
  "hallSwitch",
  "humidity",
  "icm",
  "pressure",
  "pressureSwitch",
  "gpsHeading",
  "reel",
  "wheel",
  "pivotRun",
  "pivotDirection",
  "pivotWet",
  "pivotEndGun",
  "pivotHeading",
  "pivotSpeed",
  "pivotTimeToStop",
  "runEta",
  "temperature",
  "depth",
]

/**
 * Full badge render order for the expanded status view of a given device
 * type: per-type priority sensors first, then the remaining global fallback
 * order with the per-type ones filtered out.
 *
 * Pure. Stable order for a given installation type (no dependency on which
 * badges are populated — that's the caller's job).
 */
export function getExpandedBadgeKeys({
  deviceInstallationType,
}: {
  deviceInstallationType: InstallationType
}): PrimaryStatusBadgeKey[] {
  // Tolerate unknown installation types — fall back to the global order
  // alone instead of throwing on `[...undefined]`.
  const priority =
    PRIMARY_BADGE_KEYS_BY_TYPE[deviceInstallationType] ?? []
  const prioritySet = new Set<PrimaryStatusBadgeKey>(priority)
  return [
    ...priority,
    ...EXPANDED_BADGE_FALLBACK_ORDER.filter((key) => !prioritySet.has(key)),
  ]
}

// ===========================================================================
// Center-pivot helpers
//
// A center pivot is one machine but two paired devices (controller + monitor).
// These helpers combine status from both halves so the unified pivot row can
// match the visual contract of a standalone device row.
// ===========================================================================

/**
 * Priority order for the primary badges shown on a unified center-pivot row.
 *
 * `pivotRun` is the headline signal (always shown). When the pivot is
 * actively running, `pivotTimeToStop` (run ETA) sits next so users can see
 * how long until the next stop without expanding the row. `pivotDirection`
 * and `pivotEndGun` follow as controller-intent signals; `pressure` is the
 * monitor's running-state validation and ranks last.
 */
const PIVOT_PRIMARY_BADGE_KEYS: PrimaryStatusBadgeKey[] = [
  "pivotRun",
  "pivotTimeToStop",
  "pivotDirection",
  "pivotEndGun",
  "pressure",
]

/**
 * Pick which badges to render in the compact row for a unified center pivot.
 *
 * Sources each key from whichever half's badge set has it (controller usually
 * provides run/direction/end-gun; monitor provides pressure). When the run
 * badge is energized — same `$success` colorScheme convention as the relay
 * badge — bump the cap by one so we can show run + the next signal together
 * (mirrors the pump relay-on rule).
 */
export function selectPrimaryPivotBadgeKeys({
  controllerBadges,
  monitorBadges,
}: {
  controllerBadges: SensorStatusBadges | undefined
  monitorBadges: SensorStatusBadges | undefined
}): PrimaryStatusBadgeKey[] {
  const lookup = (key: PrimaryStatusBadgeKey) =>
    controllerBadges?.[key] ?? monitorBadges?.[key]

  const available = PIVOT_PRIMARY_BADGE_KEYS.filter((key) => Boolean(lookup(key)))
  const isRunning =
    (controllerBadges?.pivotRun ?? monitorBadges?.pivotRun)?.colorScheme ===
    "$success"
  // The compact row is a single non-wrapping line, so the cap has to stay
  // small: run + one more signal when running (the ETA outranks the rest).
  // A controller reporting run/direction/end-gun plus a monitor with
  // pressure would otherwise push the row off screen; the expanded card
  // below the row shows the full badge set.
  const cap = isRunning ? MAX_PRIMARY_BADGES + 1 : MAX_PRIMARY_BADGES
  return available.slice(0, cap)
}

/**
 * Resolve a single badge entry for a unified pivot row, preferring the
 * controller's value over the monitor's. The pivot row composes its compact
 * StatusBadges from the merged set returned by this helper for each key in
 * `selectPrimaryPivotBadgeKeys`.
 */
export function getMergedPivotBadge(
  key: PrimaryStatusBadgeKey,
  controllerBadges: SensorStatusBadges | undefined,
  monitorBadges: SensorStatusBadges | undefined,
): SensorStatusBadges[PrimaryStatusBadgeKey] | undefined {
  return controllerBadges?.[key] ?? monitorBadges?.[key]
}

/**
 * Most recent check-in timestamp across both halves, or `undefined` if
 * neither has reported. Used for the "as of …" timestamp on the unified row.
 */
export function getMostRecentCheckIn(
  controllerStatus: { deviceEventTimestamp?: string | undefined } | undefined,
  monitorStatus: { deviceEventTimestamp?: string | undefined } | undefined,
): string | undefined {
  const controllerTime = controllerStatus?.deviceEventTimestamp
  const monitorTime = monitorStatus?.deviceEventTimestamp

  if (
    typeof controllerTime === "string" &&
    controllerTime.length > 0 &&
    typeof monitorTime === "string" &&
    monitorTime.length > 0
  ) {
    return new Date(controllerTime) > new Date(monitorTime)
      ? controllerTime
      : monitorTime
  }
  return controllerTime ?? monitorTime
}

/**
 * Worst battery state across both halves (BES > BCL > BEW > BEN). Returned as
 * the raw state code so the existing `BatteryCriticalWarning` and the
 * row-state ladder can both consume it.
 */
const BATTERY_PRIORITY: Record<string, number> = {
  BCL: 1,
  BEN: 3,
  BES: 0,
  BEW: 2,
}

export function getWorstBatteryState<T extends string>(
  controllerBattery: T | null | undefined,
  monitorBattery: T | null | undefined,
): T | null | undefined {
  if (
    typeof controllerBattery === "string" &&
    controllerBattery.length > 0 &&
    typeof monitorBattery === "string" &&
    monitorBattery.length > 0
  ) {
    const c = BATTERY_PRIORITY[controllerBattery] ?? 999
    const m = BATTERY_PRIORITY[monitorBattery] ?? 999
    return c < m ? controllerBattery : monitorBattery
  }
  return controllerBattery ?? monitorBattery
}

/**
 * Derive the visual state for the leading status dot on a unified pivot row.
 *
 * Combines per-half `getDeviceRowState` results using the same severity
 * ladder (worst-of-both), with two pivot-specific rules:
 *   - If both halves are absent → `unconfigured` (the pivot has no devices).
 *   - If a stoppedShort signal is present on either half (rare on pivots but
 *     possible if the monitor exposes it), the worst-of ladder already
 *     bubbles it up.
 */
type PivotHalfPick = Pick<
  DeviceStatusData,
  | "badges"
  | "batteryState"
  | "deviceInstallationType"
  | "deviceStatus"
  | "isInMotion"
  | "isStoppedShort"
  | "unreachableUntil"
>

export function getPivotRowState({
  controllerStatus,
  monitorStatus,
  now,
}: {
  controllerStatus: PivotHalfPick | undefined
  monitorStatus: PivotHalfPick | undefined
  now?: Date
}): DeviceRowState {
  if (!controllerStatus && !monitorStatus) {
    return "unconfigured"
  }

  const states: DeviceRowState[] = []
  if (controllerStatus) {
    states.push(getDeviceRowState({ ...controllerStatus, now }))
  }
  if (monitorStatus) {
    states.push(getDeviceRowState({ ...monitorStatus, now }))
  }

  // Severity ladder, highest first — mirrors getDeviceRowState's precedence.
  // `idle` ranks below `online` so an active half (controller commanding run,
  // monitor reporting motion) bubbles up over a stopped one.
  const ORDER: DeviceRowState[] = [
    "stoppedShort",
    "unconfigured",
    "unreachable",
    "batteryCritical",
    "offline",
    "online",
    "idle",
  ]
  for (const candidate of ORDER) {
    if (states.includes(candidate)) {
      return candidate
    }
  }
  return "idle"
}
